import type { TrustBadge } from '@/types/firestore';

interface TrustBadgesProps {
  badges: TrustBadge[];
}

// Built-in badge images (SVG data URLs for common trust badges)
const BADGE_IMAGES: Record<string, { svg: string; defaultLabel: string }> = {
  bbb: {
    defaultLabel: 'BBB Accredited',
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 40" fill="none">
      <rect width="80" height="40" rx="4" fill="#005A8C"/>
      <text x="40" y="25" font-family="Arial" font-size="12" font-weight="bold" fill="white" text-anchor="middle">BBB A+</text>
    </svg>`,
  },
  licensed: {
    defaultLabel: 'Licensed',
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 40" fill="none">
      <rect width="80" height="40" rx="4" fill="#16a34a"/>
      <text x="40" y="25" font-family="Arial" font-size="11" font-weight="bold" fill="white" text-anchor="middle">LICENSED</text>
    </svg>`,
  },
  insured: {
    defaultLabel: 'Fully Insured',
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 40" fill="none">
      <rect width="80" height="40" rx="4" fill="#2563eb"/>
      <text x="40" y="25" font-family="Arial" font-size="11" font-weight="bold" fill="white" text-anchor="middle">INSURED</text>
    </svg>`,
  },
  bonded: {
    defaultLabel: 'Bonded',
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 40" fill="none">
      <rect width="80" height="40" rx="4" fill="#7c3aed"/>
      <text x="40" y="25" font-family="Arial" font-size="11" font-weight="bold" fill="white" text-anchor="middle">BONDED</text>
    </svg>`,
  },
};

function getBadgeImage(badge: TrustBadge): string {
  if (badge.image_url) {
    return badge.image_url;
  }
  const builtIn = BADGE_IMAGES[badge.type];
  if (builtIn) {
    return `data:image/svg+xml,${encodeURIComponent(builtIn.svg)}`;
  }
  return '';
}

function getBadgeLabel(badge: TrustBadge): string {
  if (badge.label) return badge.label;
  const builtIn = BADGE_IMAGES[badge.type];
  return builtIn?.defaultLabel ?? badge.type;
}

export function TrustBadges({ badges }: TrustBadgesProps) {
  if (!badges || badges.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center justify-center gap-3">
      {badges.map((badge, index) => {
        const imageUrl = getBadgeImage(badge);
        const label = getBadgeLabel(badge);

        return (
          <div
            key={`${badge.type}-${index}`}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background/50 backdrop-blur-sm border border-border/40"
          >
            {imageUrl && (
              <img
                src={imageUrl}
                alt={label}
                className="h-6 w-auto object-contain"
              />
            )}
            <span className="text-xs font-medium text-muted-foreground">
              {label}
            </span>
          </div>
        );
      })}
    </div>
  );
}
